import { useMemo, useState } from "react";
import { UnifiedHistoryRow } from "../../view/contents/history/backup.history";
import {
  getSortedRowModel,
  PaginationState,
  SortingState,
} from "@tanstack/react-table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Pagination from "../common/table.pagination";
import { NewTableContainer } from "../../style/contents/backup/backup";
import { useForm } from "react-hook-form";

interface Props {
  data: UnifiedHistoryRow[];
  pageSize: number;
  paginationEnabled?: boolean;
  onUpdateSuccess: () => void; // ✅ 추가
  isLoading?: boolean; // ✅ 추가
}

// util
const formatDate = (value: number | string | null | undefined) => {
  if (!value) return "-";

  let date: Date;

  // 1️⃣ 숫자 or 숫자 문자열 (timestamp)
  if (
    typeof value === "number" ||
    (typeof value === "string" && /^\d+$/.test(value))
  ) {
    date = new Date(Number(value));
  }
  // 2️⃣ 일반 날짜 문자열
  else {
    date = new Date(value);
  }

  // ❌ Invalid Date 방어
  if (isNaN(date.getTime())) return "-";

  // return date.toLocaleString("ko-KR", {
  //   year: "numeric",
  //   month: "2-digit",
  //   day: "2-digit",
  //   hour: "2-digit",
  //   minute: "2-digit",
  //   second: "2-digit",
  // });

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// util
const removeDriveLetter = (path: string) => {
  if (!path) return "-";
  return (path || "").replace(/^[A-Za-z]:[\\/]/, "");
};
const getFileNameSafe = (path: string) => {
  if (!path) return "-";
  return path.split(/[/\\]/).pop();
};

const HistoryList = ({ data, pageSize, paginationEnabled = true, onUpdateSuccess, isLoading }: Props) => {
  const { handleSubmit } = useForm();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });

  const [sorting, setSorting] = useState<SortingState>([
    { id: "userName", desc: false },
  ]);

  const columns = useMemo<ColumnDef<UnifiedHistoryRow>[]>(
    () => [
      {
        header: "날짜",
        accessorKey: "createdDate",
        cell: (info) => formatDate(info.getValue() as number),
        size: 250,
        enableSorting: true, // ❌ 정렬 비활성화
      },
      {
        header: "동작",
        accessorKey: "category",
        cell: (info) => (info.getValue() === "BACKUP" ? "백업" : "다운로드"),
        size: 250,
        enableSorting: true, // ✅ 정렬 가능
      },
      {
        header: "구분",
        accessorKey: "type",
        cell: (info) => info.getValue(),
        size: 180,
        enableSorting: true, // ✅ 정렬 가능
      },
      {
        header: "백업 다운로드 파일 이름",
        accessorKey: "name",
        cell: ({ row }) => {
          const { name, type } = row.original;
          const displayName =
            type === "file" ? removeDriveLetter(name) : name || "-";

          return <span className="history-file-name">{displayName}</span>;
        },
        size: 700,
      },
      // {
      //   header: "부서이름",
      //   accessorKey: "deviceId",
      //   cell: (info) => (info.getValue() ? info.getValue() : "-"),
      //   size: 180,
      //   enableSorting: false, // ❌ 정렬 비활성화
      // },
      {
        header: "사용자 이름",
        accessorKey: "userName",
        cell: (info) => (info.getValue() ? info.getValue() : "-"),
        size: 280,
        enableSorting: false, // ❌ 정렬 비활성화
      },
      {
        header: "사용자 ID",
        accessorKey: "userAccount",
        cell: (info) => info.getValue(),
        size: 180,
        enableSorting: false, // ❌ 정렬 비활성화
      },
      {
        header: "컴퓨터 이름",
        accessorKey: "deviceId",
        cell: (info) => info.getValue(),
        size: 180,
        enableSorting: false, // ❌ 정렬 비활성화
      },
      // {
      //   header: "백업 용량",
      //   accessorKey: "createdDate",
      //   cell: (info) => info.getValue(),
      //   size: 180,
      //   enableSorting: false, // ❌ 정렬 비활성화
      // },
      {
        header: "IP주소",
        accessorKey: "ipAddress",
        cell: (info) => (info.getValue() ? info.getValue() : "-"),
        size: 180,
        enableSorting: false, // ❌ 정렬 비활성화
      },
      {
        header: "백업 결과",
        accessorKey: "state",
        cell: (info) => info.getValue(),
        size: 180,
        enableSorting: false, // ❌ 정렬 비활성화
      },
    ],
    [data, pagination],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      pagination,
      sorting,
    },
    enableSorting: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: paginationEnabled ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(), // ✅ 이 줄 추가!!
    onPaginationChange: setPagination,
  });

  const totalRowCount = table.getRowCount();

  const onSubmit = async (data: any) => {};

  return (
    <NewTableContainer className="new_design_ history-table">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="table-head-wrap">
          <div className="left">
            <span className="table-total-row">총 {totalRowCount}</span>
          </div>
        </div>
        {isLoading ? (
          <div className="loading">데이터를 불러오는 중입니다...</div>
        ) : (
          <div className="fixed-height">
            <table>
              <thead>
                {table.getHeaderGroups().map((item) => (
                  <tr key={item.id}>
                    {item.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          width: `${header.getSize()}px`,
                          cursor: header.column.getCanSort()
                            ? "pointer"
                            : undefined,
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {{
                          asc: (
                            <span className="up">
                              <img src="/assets/left-arrow.png" alt="" />
                            </span>
                          ),
                          desc: (
                            <span className="down">
                              <img src="/assets/left-arrow.png" alt="" />
                            </span>
                          ),
                        }[header.column.getIsSorted() as string] ?? null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="no-data-row">
                      🔍 검색 결과가 없습니다.
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </form>

      {paginationEnabled && table.getPageCount() > 0 && (
        <Pagination
          page={pagination.pageIndex}
          pages={table.getPageCount()}
          onPageChange={(page) => {
            table.setPageIndex(page); // 실제 table 내부 페이지 인덱스 업데이
          }}
        />
      )}
    </NewTableContainer>
  );
};

export default HistoryList;
